import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Check, Loader2, LocateFixed, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const KAFR_NABL = { lat: 35.6146, lng: 36.5637 };

const selectedLocationIcon = () => L.divIcon({
  className: "",
  html: '<div style="width:34px;height:34px;border-radius:50% 50% 50% 8px;transform:rotate(-45deg);background:#7B284D;border:4px solid white;box-shadow:0 7px 20px rgba(63,81,47,.3)"><div style="width:8px;height:8px;border-radius:50%;background:white;margin:9px"></div></div>',
  iconSize: [34, 34],
  iconAnchor: [17, 30],
});

export default function LocationPickerMap({ open, onOpenChange, value, onConfirm }) {
  const mapElement = useRef(null);
  const mapInstance = useRef(null);
  const marker = useRef(null);
  const valueAtOpen = useRef(value);
  valueAtOpen.current = value;
  const [point, setPoint] = useState(value || KAFR_NABL);
  const [hasChosen, setHasChosen] = useState(Boolean(value));
  const [locating, setLocating] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    let activeMap = null;
    let resizeObserver = null;
    let secondResizeTimer = null;
    setMapReady(false);
    setMapError(false);

    // Radix renders the dialog in a portal. Waiting for the opening frame makes
    // sure Leaflet receives a visible container with real dimensions.
    const initializeTimer = setTimeout(() => {
      const container = mapElement.current;
      if (cancelled || !container) {
        if (!cancelled) setMapError(true);
        return;
      }

      try {
        const initialValue = valueAtOpen.current;
        const start = initialValue || KAFR_NABL;
        setPoint(start);
        setHasChosen(Boolean(initialValue));

        const map = L.map(container, {
          zoomControl: true,
          attributionControl: false,
        }).setView([start.lat, start.lng], initialValue ? 17 : 14);
        activeMap = map;
        mapInstance.current = map;

        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          crossOrigin: true,
        }).addTo(map);

        const pin = L.marker([start.lat, start.lng], {
          icon: selectedLocationIcon(),
          draggable: true,
        }).addTo(map);
        marker.current = pin;

        const choose = ({ lat, lng }) => {
          const next = { lat, lng };
          pin.setLatLng([lat, lng]);
          setPoint(next);
          setHasChosen(true);
        };

        map.on("click", (event) => choose(event.latlng));
        pin.on("dragend", () => choose(pin.getLatLng()));
        map.invalidateSize();
        setMapReady(true);

        secondResizeTimer = setTimeout(() => map.invalidateSize(), 300);
        if (typeof ResizeObserver !== "undefined") {
          resizeObserver = new ResizeObserver(() => map.invalidateSize());
          resizeObserver.observe(container);
        }
      } catch {
        setMapError(true);
      }
    }, 180);

    return () => {
      cancelled = true;
      clearTimeout(initializeTimer);
      clearTimeout(secondResizeTimer);
      resizeObserver?.disconnect();
      if (activeMap) activeMap.remove();
      mapInstance.current = null;
      marker.current = null;
    };
  }, [open, retryKey]);

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("تحديد الموقع غير مدعوم في هذا الجهاز");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const next = { lat: coords.latitude, lng: coords.longitude };
        setPoint(next);
        setHasChosen(true);
        marker.current?.setLatLng([next.lat, next.lng]);
        mapInstance.current?.setView([next.lat, next.lng], 17, { animate: true });
        setLocating(false);
      },
      () => {
        setLocating(false);
        toast.error("تعذر تحديد موقعك، تأكد من السماح بالوصول للموقع");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  const confirm = () => {
    if (!hasChosen) {
      toast.error("حدد موقعك على الخريطة أولًا");
      return;
    }
    onConfirm(point);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-[28px] border-0 p-0">
        <DialogHeader className="px-5 pb-0 pr-14 pt-5 text-start sm:px-6 sm:pr-16 sm:pt-6">
          <div className="flex items-center gap-3 pl-8">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
              <MapPin className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-extrabold">حدد موقع التوصيل</DialogTitle>
              <DialogDescription className="mt-1 text-xs leading-5">
                اضغط على موقعك في الخريطة أو اسحب العلامة، ثم أكد أن النقطة صحيحة.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="px-3 sm:px-6">
          <div className="relative overflow-hidden rounded-[22px] border border-border bg-muted">
            <div
              ref={mapElement}
              data-testid="location-picker-map"
              className="relative z-0 w-full"
              style={{ height: "clamp(300px, 48vh, 420px)" }}
            />
            {!mapReady && !mapError && (
              <div className="absolute inset-0 z-[600] grid place-items-center bg-muted">
                <div className="text-center text-xs font-bold text-muted-foreground">
                  <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-primary" />
                  جارٍ تحميل الخريطة…
                </div>
              </div>
            )}
            {mapError && (
              <div className="absolute inset-0 z-[600] grid place-items-center bg-muted p-6 text-center">
                <div>
                  <MapPin className="mx-auto h-8 w-8 text-primary" />
                  <p className="mt-3 text-sm font-extrabold">تعذر عرض الخريطة</p>
                  <p className="mt-1 text-xs text-muted-foreground">تحقق من اتصال الإنترنت ثم حاول مرة أخرى.</p>
                  <Button type="button" variant="outline" onClick={() => setRetryKey((current) => current + 1)} className="mt-4 h-10 rounded-full px-5">إعادة المحاولة</Button>
                </div>
              </div>
            )}
            {!hasChosen && (
              <div className="pointer-events-none absolute inset-x-4 bottom-4 z-[500] rounded-2xl bg-brand/90 px-4 py-3 text-center text-xs font-bold text-white shadow-brand backdrop-blur">
                اضغط على مكانك الدقيق داخل الخريطة
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 px-5 pb-5 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:pb-6">
          <Button type="button" variant="outline" onClick={useCurrentLocation} disabled={locating} className="h-11 rounded-full px-5 font-extrabold">
            {locating ? <Loader2 className="ml-2 h-4 w-4 animate-spin" /> : <LocateFixed className="ml-2 h-4 w-4" />}
            استخدام موقعي الحالي
          </Button>
          <div className="flex items-center gap-2">
            {hasChosen && <span className="hidden items-center gap-1 text-[10px] font-bold text-success sm:flex"><Check className="h-3.5 w-3.5" /> تم تحديد النقطة</span>}
            <Button data-testid="confirm-location" type="button" onClick={confirm} disabled={!hasChosen} className="h-11 flex-1 rounded-full px-6 font-extrabold sm:flex-none">
              <Check className="ml-2 h-4 w-4" />
              نعم، الموقع صحيح
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
